/** Reusable sessions (CPToken) grid.
 *
 * Rendered standalone on /users/sessions and embedded (fixed user filter) in
 * the user detail page. Token values are always fingerprints in the grid;
 * the raw value is only fetched on demand in the details drawer, gated by
 * `console_session_reveal`.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import {
  getGridSingleSelectOperators,
  getGridStringOperators,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridRowSelectionModel,
  type GridSortModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as sessionsApi from '@/api/sessions';
import { AUTH_TYPES, type SessionListParams, type SessionRow } from '@/api/sessions';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { useSnackbar } from '@/components/Snackbar';
import {
  ResourceDataGrid,
  type HeaderToolbarConfig,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { ValueActions } from '@/components/ValueActions/ValueActions';
// The identity family's link module — a session's booking offers the same
// actions here as it does on the user page (see features/users/userLinks).
import { sessionBookingActions } from '@/features/users/userLinks';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { SessionDetailDrawer } from './SessionDetailDrawer';

const SCOPE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'info' | 'warning'> = {
  dashboard: 'secondary',
  console: 'primary',
  user: 'info',
  booking: 'warning',
};

// Header-filter options for the singleSelect Scope / Auth columns. Values are
// exactly what the backend `access` / `auth` params expect.
const SCOPE_OPTIONS = ['dashboard', 'console', 'user', 'booking'].map((s) => ({
  value: s,
  label: s,
}));
const AUTH_OPTIONS = Object.entries(AUTH_TYPES).map(([value, label]) => ({ value, label }));
const isOperator = getGridSingleSelectOperators().filter((op) => op.value === 'is');
// The Booking column filters by exact id (backend `?booking=<id>`); a single
// 'equals' text operator carries the value to the shared URL param.
const equalsOperator = getGridStringOperators().filter((op) => op.value === 'equals');

/** Quick ± range presets for the timestamp cell popovers. */
export interface DateRangeAction {
  onRange: (from: Date, to: Date) => void;
  onReset: () => void;
}

interface Props {
  /** Extra server filters merged into every request (e.g. a fixed user id). */
  fixedFilters?: SessionListParams;
  /** Extra bits appended to the query key so caches don't collide. */
  queryKeyExtra?: readonly unknown[];
  /** Show the user column (hide when embedded on a single user's page). */
  showUser?: boolean;
  /** Height strategy forwarded to ResourceDataGrid — 'fill' (default) on the
   *  standalone page, 'auto' on the embedded user tab. */
  layout?: 'fill' | 'auto' | number;
  /** Forwarded to ResourceDataGrid (embedded tab uses a small page size). */
  defaultPageSize?: number;
  /** Grid-header icon toolbar (filters / columns / reset). */
  headerToolbar?: HeaderToolbarConfig;
  onSelectionChange?: (ids: number[]) => void;
  onChanged?: () => void;
  /** Controlled details drawer — pass both to make it URL-linkable (the page
   *  reads/writes the id in the route). Omit for internal drawer state. */
  selectedId?: number | null;
  onOpenDetails?: (id: number | null) => void;
  /** Cell-popover range presets for the Created / Expires columns (page owns
   *  the filter params). Omit to hide the presets (e.g. embedded tab). */
  createdRange?: DateRangeAction;
  expiresRange?: DateRangeAction;
  /** Controlled filter model for the Scope / Auth column filters (edited via
   *  the filter dialog, synced with the page toolbar through useGridFilters).
   *  Pass both to make those columns filterable; omit on the embedded tab. */
  filterModel?: GridFilterModel;
  onFilterModelChange?: (m: GridFilterModel) => void;
  /** Grid-presets wiring (standalone page only; the embedded tab omits all of
   *  these). Forwarded to ResourceDataGrid's controlled props. */
  orderedFields?: string[];
  sortModel?: GridSortModel;
  onSortModelChange?: (m: GridSortModel) => void;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (m: GridColumnVisibilityModel) => void;
  onColumnOrderChange?: (orderedFields: string[]) => void;
}

const DELTAS: { label: string; tooltip: string; ms: number }[] = [
  { label: '±1h', tooltip: '±1 hour', ms: 3_600_000 },
  { label: '±12h', tooltip: '±12 hours', ms: 12 * 3_600_000 },
  { label: '±24h', tooltip: '±24 hours', ms: 24 * 3_600_000 },
  { label: '±7d', tooltip: '±7 days', ms: 7 * 24 * 3_600_000 },
];

export function SessionsGrid({
  fixedFilters,
  queryKeyExtra = [],
  showUser = true,
  layout = 'fill',
  defaultPageSize,
  headerToolbar,
  onSelectionChange,
  onChanged,
  selectedId,
  onOpenDetails,
  createdRange,
  expiresRange,
  filterModel,
  onFilterModelChange,
  orderedFields,
  sortModel,
  onSortModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  onColumnOrderChange,
}: Props) {
  const [internalDrawerId, setInternalDrawerId] = useState<number | null>(null);
  const controlled = onOpenDetails !== undefined;
  const drawerId = controlled ? (selectedId ?? null) : internalDrawerId;
  const openDetails = (id: number | null) =>
    controlled ? onOpenDetails(id) : setInternalDrawerId(id);
  const snackbar = useSnackbar();
  // Scope / Auth columns are filterable (via the filter dialog) only when the
  // page manages a controlled filter model — the embedded tab has no such
  // state, so a panel filter there would send nothing to the server.
  const columnsFilterable = onFilterModelChange !== undefined;

  const listParams = useCallback(
    (params: ResourceDataGridParams) => ({
      ...fixedFilters,
      ordering: params.ordering,
      ...(params.extra as Partial<SessionListParams>),
      ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
    }),
    [fixedFilters],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      sessionsApi.list({
        ...listParams(params),
        page: params.page,
        pageSize: params.pageSize,
        ...(params.countMode ? { count_mode: params.countMode } : {}),
      }),
    [listParams],
  );

  const countOnly = useCallback(
    (params: ResourceDataGridParams) => sessionsApi.count(listParams(params)),
    [listParams],
  );

  const columns = useMemo<GridColDef<SessionRow>[]>(() => {
    const cols: GridColDef<SessionRow>[] = [];
    if (showUser) {
      cols.push({
        field: 'user',
        headerName: 'User',
        flex: 1.3,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const u = p.row.user;
          const actions: CellAction[] = [
            { key: 'open', label: 'Open user', href: `/users/${u.id}` },
          ];
          return (
            <CellActionsPopover actions={actions} title={`${u.kind} · #${u.id}`} auto>
              {u.email}
            </CellActionsPopover>
          );
        },
      });
      cols.push({
        field: 'tenant',
        headerName: 'Tenant',
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const top = p.row.tour_operator;
          if (!top) return <span style={{ color: '#888' }}>system</span>;
          return (
            <WithTenantMark environment={top.environment}>
              <RouterLink to={`/tour-operators/${top.id}`} style={{ color: '#0a1a3f' }}>
                {top.code ? `${top.name} (${top.code})` : top.name}
              </RouterLink>
            </WithTenantMark>
          );
        },
      });
    }
    cols.push({
      field: 'access',
      headerName: 'Scope',
      width: 120,
      sortable: false,
      filterable: columnsFilterable,
      type: columnsFilterable ? 'singleSelect' : undefined,
      valueOptions: SCOPE_OPTIONS,
      filterOperators: columnsFilterable ? isOperator : undefined,
      renderCell: (p) => {
        const scope = p.row.access.scope;
        return (
          <Chip
            size="small"
            color={scope ? SCOPE_COLORS[scope] ?? 'default' : 'default'}
            label={scope ?? (p.row.access.flags.join('+') || '—')}
          />
        );
      },
    });
    cols.push({
      field: 'auth_display',
      headerName: 'Auth',
      width: 110,
      sortable: false,
      filterable: columnsFilterable,
      type: columnsFilterable ? 'singleSelect' : undefined,
      valueOptions: AUTH_OPTIONS,
      filterOperators: columnsFilterable ? isOperator : undefined,
    });
    cols.push({
      field: 'token',
      headerName: 'Fingerprint',
      // Fits a full 40-char SHA-1 in 12px monospace + the badge and copy button.
      width: 400,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <code style={{ fontSize: 12 }}>{p.row.token.value}</code>
          {p.row.token.is_fingerprint && (
            <Chip size="small" variant="outlined" label="fp" sx={{ height: 18 }} />
          )}
          <Tooltip title="Copy fingerprint">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // don't open the session drawer
                void navigator.clipboard
                  .writeText(p.row.token.value)
                  .then(() => snackbar.show('Fingerprint copied to clipboard.', 'success'))
                  .catch(() => snackbar.show('Could not copy to clipboard.', 'error'));
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    });
    const rangeActions = (ts: string, range?: DateRangeAction): CellAction[] => {
      if (!range) return [];
      const t = new Date(ts).getTime();
      return [
        ...DELTAS.map(({ label, tooltip, ms }) => ({
          key: label,
          label,
          tooltip: `Filter to ${tooltip} around this timestamp`,
          onClick: () => range.onRange(new Date(t - ms), new Date(t + ms)),
        })),
        {
          key: 'reset',
          label: 'Reset filter',
          icon: <FilterAltOffIcon fontSize="small" />,
          onClick: range.onReset,
        },
      ];
    };
    const detailsAction = (id: number): CellAction => ({
      key: 'details',
      label: 'Open session details',
      icon: <OpenInNewIcon fontSize="small" />,
      onClick: () => openDetails(id),
    });

    cols.push({
      field: 'created',
      headerName: 'Created',
      width: 160,
      sortable: true,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          actions={[detailsAction(p.row.id), ...rangeActions(p.row.created, createdRange)]}
          title={formatDate(p.row.created)}
          auto
        >
          {formatRelativeTime(p.row.created)}
        </CellActionsPopover>
      ),
    });
    cols.push({
      field: 'expires',
      headerName: 'Expires',
      width: 160,
      sortable: true,
      filterable: false,
      renderCell: (p) =>
        p.row.expires ? (
          <CellActionsPopover
            actions={[detailsAction(p.row.id), ...rangeActions(p.row.expires, expiresRange)]}
            title={formatDate(p.row.expires)}
            auto
          >
            <span style={{ color: p.row.is_expired ? '#b00' : undefined }}>
              {p.row.is_expired ? 'expired' : formatRelativeTime(p.row.expires)}
            </span>
          </CellActionsPopover>
        ) : (
          '—'
        ),
    });
    // Shown on the embedded user tab too: "which booking is this token for"
    // is the whole question a booking-scoped session raises, and the column
    // does not depend on the user columns the tab drops. It is only
    // FILTERABLE where the page owns a filter model to carry `?booking=`.
    cols.push({
      field: 'booking',
      headerName: 'Booking',
      width: 140,
      sortable: false,
      filterable: columnsFilterable,
      filterOperators: columnsFilterable ? equalsOperator : undefined,
      renderCell: (p) => {
        const b = p.row.booking;
        if (!b) return '—';
        return (
          <ValueActions actions={sessionBookingActions(b)}>{b.label || `#${b.id}`}</ValueActions>
        );
      },
    });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUser, snackbar, createdRange, expiresRange, controlled, onOpenDetails, columnsFilterable]);

  return (
    // In fill mode the wrapper must relay the page's flex height down to the
    // grid (a plain Box would break the chain and collapse it).
    <Box sx={layout === 'fill' ? PAGE_FILL_SX : undefined}>
      <ResourceDataGrid<SessionRow>
        queryKey={['sessions', fixedFilters, ...queryKeyExtra]}
        queryFn={queryFn}
        countMode={{ grid: 'sessions', countOnly }}
        allowExtendedTimeout
        columns={orderedFields ? orderColumns(columns, orderedFields) : columns}
        layout={layout}
        {...(defaultPageSize ? { defaultPageSize } : {})}
        {...(headerToolbar ? { headerToolbar } : {})}
        {...(filterModel ? { filterModel } : {})}
        {...(onFilterModelChange ? { onFilterModelChange } : {})}
        {...(sortModel ? { sortModel } : {})}
        {...(onSortModelChange ? { onSortModelChange } : {})}
        {...(columnVisibilityModel ? { columnVisibilityModel } : {})}
        {...(onColumnVisibilityModelChange ? { onColumnVisibilityModelChange } : {})}
        {...(onColumnOrderChange ? { onColumnOrderChange } : {})}
        defaultSort={{ field: 'created', sort: 'desc' }}
        checkboxSelection={Boolean(onSelectionChange)}
        onSelectionChange={
          onSelectionChange
            ? (ids: GridRowSelectionModel) =>
                onSelectionChange(ids.map((i) => Number(i)).filter(Number.isFinite))
            : undefined
        }
        sx={{
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
        // Row click opens the detail drawer.
        onRowClick={(id) => openDetails(Number(id))}
      />

      {drawerId !== null && (
        <SessionDetailDrawer
          sessionId={drawerId}
          open
          onClose={() => openDetails(null)}
          onChanged={onChanged}
        />
      )}
    </Box>
  );
}
